import type { VersionedDraftKit } from '@/lib/versioned-draft';
import { canSaveVersion } from '@/lib/versioned-draft';
import type {
  NpcBehaviour,
  NpcContent,
  NpcDraftContent,
  NpcExpectedNoyau,
  NpcKind,
  VersionedDetail,
} from '@matter_extractor/shared';
import {
  NPC_BEHAVIOURS,
  NPC_SOURCES_MAX,
  NPC_SOURCES_TEXT_MAX,
  npcContentSchema,
  npcSourceSchema,
} from '@matter_extractor/shared';

/**
 * What the NPC's fiche holds while somebody types (npc.md §3, issue #210).
 *
 * The comparison that greys « Enregistrer », the numbering and the refusals are in
 * `versioned-draft.ts` and hold for every versioned object. What is here is the SHAPE of an NPC's
 * draft and the four functions that cross between it and the wire.
 *
 * ## Why the draft is not an `NpcContent`
 *
 * A version is complete and trimmed; a draft is a half-written thought - a source with a title and
 * no text yet, an address left blank, a name nobody typed. `normalizedContent` decides, once, what
 * would be sent, and it ASKS THE SCHEMA (`npcContentSchema`) rather than re-deciding: the bounds,
 * the trimming and the 100 000 characters of H7 are the server's, and a second implementation
 * would drift the day a field gains one.
 *
 * ## Sources carry a LOCAL key
 *
 * A source has no identity in the contract - it is a position in an ordered list - but a React list
 * that is reordered and edited needs one, or the textarea under the caret is remounted by a move.
 * `key` is frozen at the opening and never leaves the page: `normalized` and `payload` drop it.
 */

export interface NpcDraftSource {
  /** Local, stable across edits and moves; never written. */
  readonly key: string;
  readonly title: string;
  /** The address as typed; an empty field is « no address ». */
  readonly url: string;
  readonly text: string;
  readonly provenance: string;
}

export interface NpcDraft {
  readonly name: string;
  readonly firstName: string;
  readonly kind: NpcKind;
  readonly who: string;
  readonly howToPlay: string;
  readonly sources: readonly NpcDraftSource[];
  readonly behaviours: readonly NpcBehaviour[];
  readonly moment: string;
  readonly expectedNoyau: NpcExpectedNoyau | null;
  /** The session the NPC was born from (§4.2): carried, never typed. */
  readonly originSessionId: string | null;
}

let sourceCounter = 0;

/** A fresh local key. A counter and not a UUID: a key only has to be unique in one page. */
export function newSourceKey(): string {
  sourceCounter += 1;
  return `source-${sourceCounter}`;
}

/** An empty source, for « Ajouter une source ». */
export function newDraftSource(): NpcDraftSource {
  return { key: newSourceKey(), title: '', url: '', text: '', provenance: '' };
}

/**
 * The empty fiche of an NPC that does not exist yet (§4.1).
 *
 * The kind opens on `famous`, the first of the three radios: the field is required and a
 * form that opened on no choice would be one more thing to do before the first source. No source
 * is seeded: an NPC has at least one (the schema says `min(1)`), and the first one is the first
 * thing the page asks for.
 */
export function emptyNpcDraft(): NpcDraft {
  return {
    name: '',
    firstName: '',
    kind: 'famous',
    who: '',
    howToPlay: '',
    sources: [],
    behaviours: [],
    moment: '',
    expectedNoyau: null,
    originSessionId: null,
  };
}

/**
 * A draft from a stored content - a version's, the server draft's, or the prefill of a session.
 *
 * The parameter is `NpcDraftContent`, the shared name for a partial content: a version's content
 * is a legal one (every complete content is a legal draft content, which is what lets « Voir » and
 * « Repartir de cette version » hand a version straight in).
 */
export function npcDraftFromContent(content: NpcDraftContent): NpcDraft {
  return {
    name: content.name ?? '',
    firstName: content.firstName ?? '',
    kind: content.kind ?? 'famous',
    who: content.who ?? '',
    howToPlay: content.howToPlay ?? '',
    sources: (content.sources ?? []).map((source) => ({
      key: newSourceKey(),
      title: source.title,
      url: source.url ?? '',
      text: source.text,
      provenance: source.provenance ?? '',
    })),
    behaviours: content.behaviours ?? [],
    moment: content.moment ?? '',
    expectedNoyau: content.expectedNoyau ?? null,
    originSessionId: content.originSessionId ?? null,
  };
}

/** What the editor opens on: the server draft, else the development version, else empty (§4). */
export function openingNpcDraft(
  detail: VersionedDetail<NpcContent, NpcDraftContent> | null,
): NpcDraft {
  if (detail === null) return emptyNpcDraft();
  return npcDraftFromContent(detail.draft?.content ?? detail.developmentContent);
}

/**
 * The content a draft would become, or `null` when it could not become one - which is the whole of
 * what keeps « Enregistrer » grey on a new NPC.
 *
 * The text of a source is NOT trimmed (« mot pour mot » is the rule of the bench): the schema
 * refuses a blank one and keeps the rest as pasted. Everything else is trimmed by the schema.
 */
export function normalizedNpcContent(draft: NpcDraft): NpcContent | null {
  const parsed = npcContentSchema.safeParse({
    name: draft.name,
    firstName: draft.firstName,
    kind: draft.kind,
    who: draft.who,
    howToPlay: draft.howToPlay,
    sources: draft.sources.map((source) => ({
      title: source.title,
      url: source.url.trim() === '' ? null : source.url,
      text: source.text,
      provenance: source.provenance,
    })),
    behaviours: orderedBehaviours(draft.behaviours),
    moment: draft.moment,
    expectedNoyau: draft.expectedNoyau,
    originSessionId: draft.originSessionId,
  });
  return parsed.success ? parsed.data : null;
}

/** The behaviours in the order of the closed list, so the order of the clicks is not a change. */
function orderedBehaviours(labels: readonly NpcBehaviour[]): NpcBehaviour[] {
  return NPC_BEHAVIOURS.filter((one) => labels.includes(one));
}

/**
 * What an autosave SENDS.
 *
 * `npcDraftContentSchema` is the content `.partial()`: a field is OPTIONAL and no looser, so an
 * empty name is OMITTED and cannot be sent as `''` (a 400 on the third keystroke is the one refusal
 * an autosave must never produce). Sources go the same way, one by one: a source whose title is
 * empty or whose text is blank is not a source yet and stays in the page - and the cap on the
 * count and the total of H7 are held by the fields (`maxLength`), so what is sent always fits.
 *
 * The labels, the moment and the expected noyau are sent even when empty: an emptied list IS a
 * change the draft must record. `sources` is the exception, for a reason the body says.
 */
export function npcDraftPayload(draft: NpcDraft): NpcDraftContent {
  const sources = draft.sources
    .map((source) => ({
      title: source.title,
      url: source.url.trim() === '' ? null : source.url,
      text: source.text,
      provenance: source.provenance,
    }))
    .flatMap((source) => {
      const parsed = npcSourceSchema.safeParse(source);
      return parsed.success ? [parsed.data] : [];
    })
    .slice(0, NPC_SOURCES_MAX);
  const payload: NpcDraftContent = {
    kind: draft.kind,
    behaviours: orderedBehaviours(draft.behaviours),
    moment: draft.moment.trim(),
    expectedNoyau: draft.expectedNoyau,
    originSessionId: draft.originSessionId,
  };
  // OMITTED when none is a source yet, never `[]`: `sources` keeps its `min(1)` in the draft's
  // schema, so an emptied list sent as `[]` would be answered 400 - the one refusal an autosave must
  // never produce. The absent key IS the empty list (§4).
  if (sources.length > 0) payload.sources = sources;
  const name = draft.name.trim();
  if (name !== '') payload.name = name;
  const firstName = draft.firstName.trim();
  if (firstName !== '') payload.firstName = firstName;
  const who = draft.who.trim();
  if (who !== '') payload.who = who;
  const howToPlay = draft.howToPlay.trim();
  if (howToPlay !== '') payload.howToPlay = howToPlay;
  return payload;
}

/** Everything the versioned editor needs to run an NPC's draft (§4). */
export const NPC_DRAFT_KIT: VersionedDraftKit<NpcContent, NpcDraftContent, NpcDraft> = {
  opening: openingNpcDraft,
  fromContent: npcDraftFromContent,
  normalized: normalizedNpcContent,
  payload: npcDraftPayload,
};

/** Is « Enregistrer une nouvelle version » usable? The three refusals of §4. */
export function canSaveNpc(
  draft: NpcDraft,
  detail: VersionedDetail<NpcContent, NpcDraftContent> | null,
): boolean {
  return canSaveVersion(draft, detail, NPC_DRAFT_KIT);
}

/* ------------------------------------------------------------- the sources */

/** The characters of every text together: what H7 bounds at 100 000. */
export function draftSourcesChars(draft: Pick<NpcDraft, 'sources'>): number {
  return draft.sources.reduce((total, source) => total + source.text.length, 0);
}

/** Can another source be added? The count is a ceiling of plausibility (50). */
export function canAddSource(draft: Pick<NpcDraft, 'sources'>): boolean {
  return draft.sources.length < NPC_SOURCES_MAX;
}

/**
 * How many characters the text of one source may still hold: the room the others leave under the
 * 100 000 of H7. A `maxLength` taken from it is what keeps the total under the bound, so that no
 * refusal can come back for it.
 */
export function sourceTextRoom(draft: Pick<NpcDraft, 'sources'>, key: string): number {
  const others = draft.sources
    .filter((source) => source.key !== key)
    .reduce((total, source) => total + source.text.length, 0);
  return Math.max(0, NPC_SOURCES_TEXT_MAX - others);
}

/** One source rewritten, the others untouched. */
export function withSource(
  draft: NpcDraft,
  key: string,
  patch: Partial<Omit<NpcDraftSource, 'key'>>,
): NpcDraft {
  return {
    ...draft,
    sources: draft.sources.map((source) => (source.key === key ? { ...source, ...patch } : source)),
  };
}

/** One source moved by `delta` places; a move off either end changes nothing. */
export function withSourceMoved(draft: NpcDraft, key: string, delta: -1 | 1): NpcDraft {
  const from = draft.sources.findIndex((source) => source.key === key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= draft.sources.length) return draft;
  return withSourceAt(draft, key, to);
}

/** One source put at an index of the list WITHOUT it - what a drop computes. */
export function withSourceAt(draft: NpcDraft, key: string, index: number): NpcDraft {
  const moved = draft.sources.find((source) => source.key === key);
  if (moved === undefined) return draft;
  const rest = draft.sources.filter((source) => source.key !== key);
  rest.splice(Math.max(0, Math.min(index, rest.length)), 0, moved);
  return { ...draft, sources: rest };
}

/** A behaviour switched on or off. */
export function withBehaviourToggled(draft: NpcDraft, behaviour: NpcBehaviour): NpcDraft {
  return {
    ...draft,
    behaviours: draft.behaviours.includes(behaviour)
      ? draft.behaviours.filter((one) => one !== behaviour)
      : [...draft.behaviours, behaviour],
  };
}
